import { useState, useEffect } from "react";
import { X, Download, Share2 } from "lucide-react";

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia("(display-mode: standalone)").matches) return;

    // Check if dismissed recently
    const dismissed = localStorage.getItem("pwa-install-dismissed");
    if (dismissed && Date.now() - parseInt(dismissed) < 3 * 24 * 60 * 60 * 1000) return;

    // iOS detection
    const ua = navigator.userAgent;
    const isIOSDevice = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    setIsIOS(isIOSDevice);

    if (isIOSDevice) {
      // Show iOS install instructions after a delay
      const timer = setTimeout(() => setShowBanner(true), 5000);
      return () => clearTimeout(timer);
    }

    // Android/Desktop - listen for beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShowBanner(true), 3000);
    };

    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setShowBanner(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem("pwa-install-dismissed", Date.now().toString());
  };

  if (!showBanner) return null;

  return (
    <div
      data-testid="install-prompt"
      className="fixed bottom-16 left-3 right-3 z-[60] md:bottom-4 md:left-auto md:right-4 md:max-w-sm animate-in slide-in-from-bottom-4 duration-300"
    >
      <div className="bg-emerald-800 text-white rounded-xl shadow-2xl p-4 border border-emerald-600">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
            <Download className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm">Install App</h3>
            {isIOS ? (
              <p className="text-emerald-100 text-xs mt-1 leading-relaxed">
                Tap <Share2 className="w-3 h-3 inline mx-0.5" /> Share, then "Add to Home Screen" to install this app.
              </p>
            ) : (
              <p className="text-emerald-100 text-xs mt-1">
                Add to your home screen for quick access.
              </p>
            )}
          </div>
          <button
            onClick={handleDismiss}
            className="text-emerald-200 hover:text-white p-1"
            data-testid="install-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        {!isIOS && deferredPrompt && (
          <button
            onClick={handleInstall}
            data-testid="install-btn"
            className="mt-3 w-full bg-white text-emerald-800 font-semibold text-sm py-2.5 rounded-lg active:bg-emerald-50 transition-colors"
          >
            Install Now
          </button>
        )}
      </div>
    </div>
  );
}
